import { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import AddressForm, { AddressFormValue } from '../components/AddressForm';
import Header from '../components/Header';
import { ConfirmDialog, MessageDialog } from '../components/ui/alert-dialog-helper';
import { Button } from '../components/ui/button';
import { createDateColumn } from '../components/ui/column-helpers';
import { DataTable } from '../components/ui/data-table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

type Address = {
  id: string;
  address_line1: string;
  address_line2?: string | null;
  city: string;
  region?: string | null;
  postal_code?: string | null;
  country_code: string;
  latitude?: number | null;
  longitude?: number | null;
  address_type: string;
};

export default function Locations() {
  const [user, setUser] = useState<any>(null);
  const [rows, setRows] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Address | null>(null);
  const [form, setForm] = useState<Partial<Address>>({});

  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<Address | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => { try { const u = localStorage.getItem('user'); if (u) setUser(JSON.parse(u)); } catch {} }, []);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/addresses', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setRows(Array.isArray(data) ? data : (data.data || []));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (row?: Address) => {
    const target = row || null;
    setEditing(target);
    setForm(target ? { ...target } : { address_line1: '', city: '', country_code: 'GB', address_type: 'other', latitude: null, longitude: null });
    setDialogOpen(true);
  };

  const save = async (val: AddressFormValue) => {
    const token = localStorage.getItem('token');
    const isEdit = !!editing?.id;
    const url = isEdit ? `/api/addresses/${editing!.id}` : '/api/addresses';
    const method = isEdit ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(val) });
    const data = await res.json();
    if (!res.ok || data.success === false) throw new Error(data.message || 'Save failed');
    setDialogOpen(false);
    setEditing(null);
    await load();
  };

  const handleDeleteClick = (address: Address) => {
    setAddressToDelete(address);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!addressToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/addresses/${addressToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();

      if (res.ok && data.success !== false) {
        setDeleteDialogOpen(false);
        setAddressToDelete(null);
        await load();
      } else {
        setDeleteDialogOpen(false);
        setErrorMessage(data.message || 'Failed to delete location');
        setErrorDialogOpen(true);
      }
    } catch (err) {
      console.error('Error deleting location:', err);
      setDeleteDialogOpen(false);
      setErrorMessage('An error occurred while deleting the location');
      setErrorDialogOpen(true);
    }
  };

  const canEdit = user?.roles?.some((role: any) => ['admin', 'supervisor'].includes(role.key)) ?? false;

  const columns: ColumnDef<Address>[] = useMemo(() => ([
    { accessorKey: 'address_line1', header: 'Address Line 1' },
    { accessorKey: 'city', header: 'City' },
    { accessorKey: 'region', header: 'Region' },
    { accessorKey: 'postal_code', header: 'Postcode' },
    { accessorKey: 'country_code', header: 'Country' },
    { accessorKey: 'address_type', header: 'Type' },
    createDateColumn<Address>('created_at', 'Created At', 'datetime'),
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canEdit && (
              <DropdownMenuItem onClick={() => startEdit(row.original)}>
                <Pencil className="h-4 w-4 mr-2" /> Edit
              </DropdownMenuItem>
            )}
            {canEdit && (
              <DropdownMenuItem
                onClick={() => handleDeleteClick(row.original)}
                className="text-red-600"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]), [canEdit, handleDeleteClick]);

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="settings" />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-heading font-bold text-primary leading-tight mb-1">Locations</h2>
            <p className="text-muted-foreground">Manage saved addresses used to assign unit locations</p>
          </div>
          <Button onClick={() => startEdit(undefined)}>Add Location</Button>
        </div>

        <DataTable
          columns={columns}
          data={rows}
          loading={loading}
          searchKey="address_line1"
          searchPlaceholder="Search locations..."
          emptyMessage="No locations found."
        />

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Location' : 'Add Location'}</DialogTitle>
              <DialogDescription>Update address details</DialogDescription>
            </DialogHeader>
            <AddressForm
              initial={form as any}
              onCancel={() => setDialogOpen(false)}
              onSave={save}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title="Delete Location"
          description={`Are you sure you want to delete "${addressToDelete?.address_line1}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDelete}
          variant="destructive"
        />

        {/* Error Message Dialog */}
        <MessageDialog
          open={errorDialogOpen}
          onOpenChange={setErrorDialogOpen}
          title="Error"
          description={errorMessage}
          variant="destructive"
        />
      </main>
    </div>
  );
}


